/**
 * EQ Hub - Simple Stats Dashboard
 * 
 * DEX Standard: Premium minimalism - only show real tracked data
 * - Liked count (session tracked)
 * - Passed count (session tracked)
 * - Streak (engagement tracked)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Flame, ChevronRight } from 'lucide-react';
import { useStreak } from '../../contexts/EngagementContext';

export default function EQHub({ isOpen, onClose, likedCount = 0, archivedCount = 0 }) {
  const navigate = useNavigate();
  const streak = useStreak();
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ animation: 'eq-modal-in 300ms ease-out' }}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <h2 className="text-base font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
            Stats
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-black/40" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-5 space-y-3">
          {/* Streak - Real tracked data from engagement */}
          {streak.current > 0 && (
            <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
                    <Flame className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700">Streak</p>
                    <p className="text-xs text-amber-600/60">Days active</p>
                  </div>
                </div>
                <span className="text-2xl font-bold text-amber-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {streak.current}
                </span>
              </div>
            </div>
          )}

          {/* Liked - Session tracked */}
          <button
            onClick={() => {
              onClose();
              navigate('/app', { state: { statusFilter: 'liked' } });
            }}
            className="w-full p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200 text-left hover:shadow-md transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-medium text-emerald-700">Liked</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-emerald-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {likedCount}
                </span>
                <ChevronRight className="w-4 h-4 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </button>
          
          {/* Passed - Session tracked */}
          <button
            onClick={() => {
              onClose();
              navigate('/app', { state: { statusFilter: 'archived' } });
            }}
            className="w-full p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 text-left hover:shadow-md transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-400 flex items-center justify-center">
                  <X className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-medium text-slate-600">Passed</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-slate-500" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {archivedCount}
                </span>
                <ChevronRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes eq-modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
